import * as React from "react";

import { SessionExpiredDialog } from "lib/components/SessionExpiredDialog";
import { Notification } from "lib/components/Notification";
import { Form } from "../Form";
import { Job } from "../Job";
import style from "./style.module.css";
import { Explorer } from "../Explorer";
import {
  DockviewDefaultTab,
  DockviewReact,
  DockviewReadyEvent,
  IDockviewDefaultTabProps,
  IDockviewPanelProps,
  themeLight,
} from "dockview";
import "dockview/dist/styles/dockview.css";
import { LeftControls, RightControls } from "./controls";
import { FormStore, getStore } from "bundles/BigqueryQueryViewer/store";
import { Problem } from "../Problem";
import { PubsubContextProvider } from "bundles/BigqueryQueryViewer/lib/pubsub";
import { showChat, showExplorer, showJob, showProblem } from "./common";
import { TableComponent } from "../Table";
import { FuncComponent } from "../Func";
import { Help } from "../Help";
import { useCable } from "bundles/BigqueryQueryViewer/lib/cable";
import { Chat } from "../Chat";
import { GraphForm } from "../GraphForm";
import { observer } from "mobx-react-lite";
import { Status } from "../../constants";

const QueryTab = observer((props: IDockviewDefaultTabProps) => {
  const formStore =
    props.params.store instanceof FormStore ? props.params.store : undefined;
  const status = formStore?.queryResult?.queryStatus;
  const isExecuting =
    status === Status.Waiting ||
    status === Status.Enqueued ||
    status === Status.Running ||
    status === Status.Stopping;
  const statusClass = isExecuting
    ? style.tabExecuting
    : status === Status.Success
      ? style.tabSuccess
      : status === Status.Error
        ? style.tabError
        : "";

  return (
    <div className={`${style.tab} ${statusClass}`}>
      <DockviewDefaultTab {...props} />
    </div>
  );
});

const Default = (props: IDockviewPanelProps) => {
  return <div>{props.api.title}</div>;
};

const components = {
  default: Default,
  form: Form,
  explorer: Explorer,
  problem: Problem,
  job: Job,
  table: TableComponent,
  func: FuncComponent,
  chat: Chat,
  help: Help,
  graphForm: GraphForm,
};

export function App() {
  const store = getStore();

  const onReady = (event: DockviewReadyEvent) => {
    store.preferences.load();

    // パネルが消えたらフォーム一覧からも削除しておく
    event.api.onDidRemovePanel((e) => {
      const index = store.forms.findIndex((_) => _.id == e.id);
      if (index >= 0) {
        store.forms.splice(index, 1);
      } else {
        const index2 = store.graphForms.findIndex((_) => _.id == e.id);
        if (index2 >= 0) {
          store.graphForms.splice(index2, 1);
        }
      }
    });
    // フォームが追加/削除されたら同期する
    store.formAdded.on((form) => {
      event.api.addPanel({
        id: form.id,
        title: form.title,
        component: "form",
        position: {
          referenceGroup: "editors",
        },
        params: { store: form },
      });
    });
    store.formRemoved.on((form) => {
      const panel = event.api.panels.find((_) => _.id == form.id);
      if (panel) {
        event.api.removePanel(panel);
      }
    });
    store.graphFormAdded.on((graphForm) => {
      event.api.addPanel({
        id: graphForm.id,
        title: graphForm.title,
        component: "graphForm",
        position: {
          referenceGroup: "editors",
          direction: "right",
        },
        params: { store: graphForm },
      });
    });
    store.graphFormRemoved.on((graphForm) => {
      const panel = event.api.panels.find((_) => _.id == graphForm.id);
      if (panel) {
        event.api.removePanel(panel);
      }
    });
    // エディターを表示するパネルグループが消えたら困るので
    // 消えたら復活させる
    event.api.onDidRemoveGroup((e) => {
      if (e.id == "editors") {
        // それっぽい場所に復元
        let referencePanel: string | undefined = undefined;
        if (event.api.panels.find((_) => _.id == "problem")) {
          referencePanel = "problem";
        } else if (event.api.panels.find((_) => _.id == "job")) {
          referencePanel = "job";
        }

        event.api.addGroup({
          id: "editors",
          direction: "above",
          initialHeight: e.height,
          initialWidth: e.width,
          referencePanel,
        });
      }
    });

    // エディター表示用のパネルグループの作成
    event.api.addGroup({
      id: "editors",
      direction: "above",
    });

    // 初期のクエリエディターのパネルを表示
    event.api.addPanel({
      id: store.forms[0].id,
      title: store.forms[0].title,
      component: "form",
      position: {
        referenceGroup: "editors",
      },
      params: {
        store: store.forms[0],
      },
    });

    // 各種パネルを表示
    const explorer = showExplorer(event.api);
    showProblem(event.api);
    showJob(event.api, true);
    const chat = showChat(event.api);
    explorer.api.setSize({ width: 300 });
    chat.api.setSize({ width: 400 });
  };

  useCable(
    (message) => {
      store.list.changeQueryStatus(
        message.query_id,
        message.status,
        message.message,
      );
      store.chat.changeQueryStatus(
        message.query_id,
        message.status,
        message.message,
      );
    },
    (message) => {
      store.list.changeExportStatus(
        message.query_id,
        message.status,
        message.message,
        message.signed_url,
      );
    },
  );

  return (
    <PubsubContextProvider>
      <div className={style.root} id="dockview-root">
        <div className={style.inner}>
          <DockviewReact
            theme={themeLight}
            onReady={onReady}
            components={components}
            defaultTabComponent={QueryTab}
            leftHeaderActionsComponent={LeftControls}
            rightHeaderActionsComponent={RightControls}
          />
        </div>
        <SessionExpiredDialog />
        <Notification />
      </div>
    </PubsubContextProvider>
  );
}
